'use client';

import Link from 'next/link';
import Logo from './Logo';
import { usePathname, useSearchParams } from 'next/navigation';
import { useState, useRef, useEffect, useMemo, Suspense } from 'react';
import { useFile } from '@/app/context/FileContext';
import { TOOLS } from '@/app/lib/toolsCatalog';
import { useSidebar } from '@/app/context/SidebarContext';
import { useAuth } from '@/app/context/AuthContext';
import { useBodyScrollLock } from '@/app/hooks/useBodyScrollLock';
import { trackNavClick, trackSidebarSearch } from '@/app/lib/trackNav';
import { PUBLISHED_SEEN_EVENT, fetchLatestDecision, hasUnseenDecision } from '@/app/lib/community/publishedUpdates';

/** '/tools/banner-editor' -> 'banner-editor'. Reported as the slug so it joins on the same key
 *  `tool_view` and the admin funnel already use; a full path would not group with them. */
const toolSlug = (href: string) => href.replace(/^\/tools\//, '').split('?')[0];

interface ToolSubsection {
  name: string;
  section: string;
}

interface Tool {
  name: string;
  href: string;
  icon: 'file' | 'sparkles' | 'search' | 'database' | 'pencil' | 'terminal' | 'banner' | 'shield' | 'sign' | 'crafting' | 'potion' | 'book' | 'armor' | 'armorstand' | 'firework' | 'mob' | 'portal' | 'palette' | 'title' | 'beacon' | 'circle' | 'motd' | 'shapes' | 'skin' | 'cube' | 'text' | 'gradient' | 'anvil';
  fileType?: 'level-dat' | 'player-data';
  subsections?: ToolSubsection[];
}

interface ToolGroup {
  label: string;
  tools: Tool[];
}

/**
 * Local preview switch for the tools that are built but not launched
 * (`hidden: true` in toolsCatalog.ts, pending a design-consistency pass). Was 12; three were
 * launched on 2026-08-13, leaving 9. Don't restate the count here — `npm run verify:registration`
 * counts the catalog and checks every surface agrees with it.
 *
 * Set `NEXT_PUBLIC_SHOW_HIDDEN_TOOLS=1` in `.env.local` to see them in the nav while
 * reviewing. **Unset everywhere else, so production is unchanged** — this is a
 * preview affordance, NOT the re-launch. Actually launching a tool still means the
 * full six-surface checklist (catalog flag, this sidebar, sitemap, RelatedTools,
 * responsive spec, and the homepage grid in app/page.tsx).
 */
const SHOW_HIDDEN_TOOLS = process.env.NEXT_PUBLIC_SHOW_HIDDEN_TOOLS === '1';

const previewOnly = (tools: Tool[]): Tool[] => (SHOW_HIDDEN_TOOLS ? tools : []);

const toolGroups: ToolGroup[] = [
  {
    label: 'File Editors',
    tools: [
      {
        name: 'Level.dat Editor',
        href: '/tools/level-dat',
        icon: 'file',
        fileType: 'level-dat',
        subsections: [
          { name: 'Overview', section: 'overview' },
          { name: 'Time & Weather', section: 'time-weather' },
          { name: 'Gamerules', section: 'gamerules' },
          { name: 'Spawn & Border', section: 'spawn-border' },
          { name: 'Active Mods', section: 'active-mods' },
          { name: 'Modded Rules', section: 'modded-rules' },
          { name: 'Raw Data', section: 'raw-data' },
        ]
      },
      {
        name: 'Single Player Editor',
        href: '/tools/singleplayer-data',
        icon: 'file',
        fileType: 'level-dat',
        subsections: [
          { name: 'Summary', section: 'summary' },
          { name: 'Inventory', section: 'inventory' },
          { name: 'Raw Data', section: 'raw-data' },
        ]
      },
      {
        name: 'Server Player Editor',
        href: '/tools/player-data',
        icon: 'file',
        fileType: 'player-data',
        subsections: [
          { name: 'Summary', section: 'summary' },
          { name: 'Inventory', section: 'inventory' },
          { name: 'Raw Data', section: 'raw-data' },
        ]
      },
    ],
  },
  {
    label: 'Generators',
    tools: [
      { name: 'Command Generator', href: '/tools/command-generator', icon: 'terminal' },
      { name: 'Enchantment Builder', href: '/tools/enchantment-builder', icon: 'sparkles' },
      { name: 'Execute Builder', href: '/tools/execute-builder', icon: 'terminal' },
      { name: 'Tellraw Editor', href: '/tools/tellraw-editor', icon: 'terminal' },
      { name: 'Loot Table Builder', href: '/tools/loot-table-builder', icon: 'database' },
      { name: 'Title Generator', href: '/tools/title-generator', icon: 'title' },
      { name: 'MOTD Generator', href: '/tools/motd-generator', icon: 'motd' },
      { name: 'Sulfur Cube Archetype Builder', href: '/tools/sulfur-cube-archetype-builder', icon: 'cube' },
      ...previewOnly([
        { name: 'Colored Text Generator', href: '/tools/colored-text-generator', icon: 'text' },
        { name: 'Gradient Generator', href: '/tools/gradient-generator', icon: 'gradient' },
      ]),
    ],
  },
  {
    label: 'Design Tools',
    tools: [
      { name: 'Banner Editor', href: '/tools/banner-editor', icon: 'banner' },
      { name: 'Shield Editor', href: '/tools/shield-editor', icon: 'shield' },
      { name: 'Potion Editor', href: '/tools/potion-editor', icon: 'potion' },
      { name: 'Armor Editor', href: '/tools/armor-editor', icon: 'armor' },
      { name: 'Armor Stand Editor', href: '/tools/armor-stand', icon: 'armorstand' },
      { name: 'Mob Builder', href: '/tools/mob-builder', icon: 'mob' },
      { name: 'Sign Editor', href: '/tools/sign-generator', icon: 'sign' },
      { name: 'Book Editor', href: '/tools/book-editor', icon: 'book' },
      { name: 'Firework Editor', href: '/tools/firework-editor', icon: 'firework' },
      { name: 'Skin Editor', href: '/tools/skin-editor', icon: 'pencil' },
      ...previewOnly([
        { name: 'Circle Generator', href: '/tools/pixel-circle-generator', icon: 'shapes' },
      ]),
    ],
  },
  {
    label: 'Reference',
    tools: [
      { name: 'Recipe Viewer', href: '/tools/recipe-viewer', icon: 'crafting' },
      { name: 'UUID Lookup', href: '/tools/uuid-lookup', icon: 'search' },
      { name: 'Server Status Checker', href: '/tools/server-status', icon: 'motd' },
      { name: 'Skin Stealer', href: '/tools/skin-stealer', icon: 'skin' },
      { name: 'Minecraft ID List', href: '/tools/id-list', icon: 'database' },
      { name: 'Anvil Calculator', href: '/tools/anvil-calculator', icon: 'anvil' },
      { name: 'Nether Portal Calculator', href: '/tools/nether-portal-calculator', icon: 'portal' },
      ...previewOnly([
        { name: 'Beacon Range Calculator', href: '/tools/beacon-range-calculator', icon: 'beacon' },
        { name: 'Color Codes', href: '/tools/color-codes', icon: 'palette' },
      ]),
    ],
  },
];

function ToolIcon({ icon }: { icon: Tool['icon'] }) {
  if (icon === 'sign') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="11" rx="1" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        <line x1="12" y1="15" x2="12" y2="21" strokeWidth={2} strokeLinecap="round" />
      </svg>
    );
  }
  if (icon === 'banner') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <line x1="12" y1="2" x2="12" y2="4" strokeWidth={2} strokeLinecap="round" />
        <line x1="7" y1="3" x2="17" y2="3" strokeWidth={2} strokeLinecap="round" />
        <path strokeLinejoin="round" strokeWidth={2} d="M7 3h10v14l-5-3-5 3V3z" />
      </svg>
    );
  }
  if (icon === 'shield') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    );
  }
  if (icon === 'pencil') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    );
  }
  if (icon === 'skin') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    );
  }
  if (icon === 'sparkles') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    );
  }
  if (icon === 'search') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    );
  }
  if (icon === 'crafting') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
        <line x1="3" y1="9" x2="21" y2="9" strokeWidth={2} />
        <line x1="3" y1="15" x2="21" y2="15" strokeWidth={2} />
        <line x1="9" y1="3" x2="9" y2="21" strokeWidth={2} />
        <line x1="15" y1="3" x2="15" y2="21" strokeWidth={2} />
      </svg>
    );
  }
  if (icon === 'potion') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3h6m-5 0v4.5L4 17a2 2 0 002 2h12a2 2 0 002-2l-6-9.5V3" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 14h10" />
      </svg>
    );
  }
  if (icon === 'book') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    );
  }
  if (icon === 'armor') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2L4 5v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V5l-8-3z" />
      </svg>
    );
  }
  if (icon === 'armorstand') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v3m0 0v5m0-5H7m5 0h5m-5 5v5m0 0H9m3 0h3M7 21h10M12 18v3" />
      </svg>
    );
  }
  if (icon === 'firework') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    );
  }
  if (icon === 'mob') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    );
  }
  if (icon === 'database') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
      </svg>
    );
  }
  if (icon === 'terminal') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  if (icon === 'motd') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth={2} />
        <path strokeWidth={2} strokeLinecap="round" d="M7 10h6M7 14h10" />
      </svg>
    );
  }
  if (icon === 'cube') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
      </svg>
    );
  }
  if (icon === 'shapes') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="8" cy="8" r="4.5" strokeWidth={2} />
        <rect x="12.5" y="12" width="8" height="8" rx="1" strokeWidth={2} />
        <path strokeWidth={2} strokeLinejoin="round" d="M8 12.5l4 7H4l4-7z" />
      </svg>
    );
  }
  if (icon === 'circle') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="8" strokeWidth={2} />
        <path strokeWidth={2} strokeLinecap="round" d="M4 12h16M12 4v16" opacity="0.4" />
      </svg>
    );
  }
  if (icon === 'beacon') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9h6l3 11H6l3-11z" />
        <path strokeLinecap="round" strokeWidth={2} d="M12 6V3" />
      </svg>
    );
  }
  if (icon === 'anvil') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h14l4-1v2a4 4 0 01-4 4h-2v4H9v-4H8a5 5 0 01-5-5zM6 19h12M9 15l-2 4m8-4l2 4" />
      </svg>
    );
  }
  if (icon === 'portal') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <ellipse cx="12" cy="12" rx="5" ry="9" strokeWidth={2} />
        <path strokeLinecap="round" strokeWidth={2} d="M4 12h16M12 4v16" />
      </svg>
    );
  }
  if (icon === 'title') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M9 6v12M6 18h6m4-4h4m-4 4h4" />
      </svg>
    );
  }
  if (icon === 'text') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7V5h16v2M12 5v14M9 19h6" />
      </svg>
    );
  }
  if (icon === 'gradient') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 17l6-10 4 6 2-3 4 7z" />
      </svg>
    );
  }
  if (icon === 'palette') {
    return (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM11 15h10M18 3h1a2 2 0 012 2v8" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function SidebarFooter({ collapsed, closeMobile }: { collapsed: boolean; closeMobile: () => void }) {
  const { user, profile, isAdmin, signOut, loading } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  // Show skeleton while auth is loading, or while we have a user but profile hasn't loaded yet
  if (loading || (user && !profile)) {
    return (
      <div className="border-t border-zinc-800 p-3">
        <div className={`flex items-center gap-2 px-3 py-2 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-5 h-5 rounded-full bg-zinc-800 flex-shrink-0" />
          {!collapsed && <div className="h-4 w-20 bg-zinc-800 rounded" />}
        </div>
      </div>
    );
  }

  // Not logged in — show sign-in and create account links
  if (!user) {
    if (collapsed) {
      return (
        <div className="border-t border-zinc-800 p-3">
          <Link
            href="/login"
            onClick={closeMobile}
            className="flex items-center justify-center px-3 py-2 rounded-lg text-zinc-400 hover:bg-zinc-800/50 hover:text-white transition-colors"
            title="Sign In"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </Link>
        </div>
      );
    }
    return (
      <div className="border-t border-zinc-800 p-3 flex items-center justify-center gap-2">
        <Link
          href="/login"
          onClick={closeMobile}
          className="text-sm text-zinc-400 hover:text-white transition-colors"
        >
          Sign In
        </Link>
        <span className="text-zinc-500 text-sm">/</span>
        <Link
          href="/signup?ref=sidebar"
          onClick={closeMobile}
          className="text-sm text-zinc-400 hover:text-white transition-colors"
        >
          Create Account
        </Link>
      </div>
    );
  }

  // Logged in
  const initial = profile!.username.charAt(0).toUpperCase();

  return (
    <div className="border-t border-zinc-800 p-3 relative" ref={dropdownRef}>
      {/* Account button */}
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors ${
          collapsed ? 'justify-center' : ''
        }`}
        title={collapsed ? profile!.username : undefined}
      >
        <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-emerald-950 text-xs font-bold flex-shrink-0">
          {initial}
        </div>
        {!collapsed && (
          <span className="text-sm truncate flex-1 text-left">{profile!.username}</span>
        )}
        {!collapsed && (
          <svg className={`w-4 h-4 text-zinc-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        )}
      </button>

      {/* Dropdown menu — opens upward */}
      {dropdownOpen && (
        <div className={`absolute bottom-full mb-1 ${collapsed ? 'left-full ml-1' : 'left-3 right-3'} bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 z-50`}>
          <Link
            href="/account"
            onClick={() => { setDropdownOpen(false); closeMobile(); }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700/50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Account Settings
          </Link>

          <Link
            href="/saved"
            onClick={() => { setDropdownOpen(false); closeMobile(); }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700/50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            My Library
          </Link>

          {isAdmin && (
            <>
              <div className="border-t border-zinc-700 my-1" />
              <Link
                href="/admin"
                onClick={() => { setDropdownOpen(false); closeMobile(); }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700/50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Admin Dashboard
              </Link>
            </>
          )}

          <div className="border-t border-zinc-700 my-1" />
          <button
            onClick={async () => {
              setDropdownOpen(false);
              await signOut();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-zinc-700/50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function SidebarContent() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { uploadedFile, hasModsDetected, hasSinglePlayerData } = useFile();
  const { collapsed: collapsedPref, mobileOpen, toggle, closeMobile } = useSidebar();
  // The icon-only rail is a desktop (lg+) preference. The drawer below lg always shows labels —
  // otherwise a rail collapsed on a landscape tablet opened as a full drawer of bare icons.
  const collapsed = collapsedPref && !mobileOpen;
  const { user } = useAuth();

  // A dot on My Library when a moderator has decided on one of the user's designs since they last
  // looked (app/lib/community/publishedUpdates.ts). Re-checked on navigation from a 10-minute cache.
  const [libraryUpdate, setLibraryUpdate] = useState(false);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetchLatestDecision(user.id)
      .then(latest => { if (!cancelled) setLibraryUpdate(hasUnseenDecision(user.id, latest)); })
      .catch(() => undefined);
    const clear = () => setLibraryUpdate(false);
    window.addEventListener(PUBLISHED_SEEN_EVENT, clear);
    return () => {
      cancelled = true;
      window.removeEventListener(PUBLISHED_SEEN_EVENT, clear);
    };
  }, [user, pathname]);
  const showLibraryDot = !!user && libraryUpdate;
  const [expandedTools, setExpandedTools] = useState<string[]>(['/tools/level-dat', '/tools/player-data']);
  const [query, setQuery] = useState('');

  const currentSection = searchParams.get('section') || 'overview';

  // Live tool-search filter. Match on each tool's name AND its catalog
  // description (so "coordinate" finds the Nether Portal Calculator). Empty
  // query → the full grouped list, unchanged.
  const q = query.trim().toLowerCase();
  const visibleGroups = useMemo(() => {
    if (!q) return toolGroups;
    return toolGroups
      .map(group => ({
        ...group,
        tools: group.tools.filter(tool =>
          tool.name.toLowerCase().includes(q) ||
          (TOOLS[tool.href.replace('/tools/', '')]?.description ?? '').toLowerCase().includes(q),
        ),
      }))
      .filter(group => group.tools.length > 0);
  }, [q]);

  // Report a search once the user PAUSES, never per keystroke — `onChange` fires per character,
  // so an un-debounced event would send one row per letter, which is the flood shape. The 2-char
  // minimum drops the noise of a single stray key.
  //
  // The result COUNT is the point: `results: 0` separates "nobody searches" from "people search
  // and find nothing", and those have opposite fixes — remove the box, or fix the matcher above.
  // ⚠️ The query text itself is deliberately never sent; only its length.
  //
  // ⚠️ A pending report is FLUSHED when a result is clicked (see the tool link's onClick). Without
  // that, a search followed by a click inside the debounce was never reported at all: the click
  // navigates, the route-change effect clears the query, and this effect's cleanup cancels the
  // timer. That dropped exactly the searches that WORKED and kept the abandoned and zero-result
  // ones, skewing the one metric this event exists to measure.
  const pendingSearch = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (q.length < 2) return;
    // One-shot: the click flush calls `send` directly but cannot cancel this effect's timer, and
    // the timer still fires whenever the query survives the click (a ctrl/cmd-click to a new tab,
    // or clicking the tool already open) — which double-counted exactly the searches that worked.
    let sent = false;
    const send = () => {
      if (sent) return;
      sent = true;
      pendingSearch.current = null;
      trackSidebarSearch(q.length, visibleGroups.reduce((n, g) => n + g.tools.length, 0));
    };
    pendingSearch.current = send;
    const t = setTimeout(send, 1200);
    return () => {
      clearTimeout(t);
      if (pendingSearch.current === send) pendingSearch.current = null;
    };
  }, [q, visibleGroups]);

  // Close mobile menu + clear the search on route change
  useEffect(() => {
    closeMobile();
    setQuery('');
  }, [pathname, closeMobile]);

  // Lock page scroll while the full-width mobile drawer is open, so a scroll
  // gesture moves the menu, never the page behind it.
  useBodyScrollLock(mobileOpen);

  const hasFileForTool = (fileType: string): boolean => {
    if (!uploadedFile) return false;
    return uploadedFile.type === fileType;
  };

  const toggleToolExpand = (href: string) => {
    setExpandedTools(prev =>
      prev.includes(href)
        ? prev.filter(h => h !== href)
        : [...prev, href]
    );
  };

  const isToolActive = (href: string) => pathname === href || pathname.startsWith(href + '/');

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={closeMobile}
        />
      )}

      <aside
        // Lifts the account / Sign In row above a bottom AdSense anchor ad (see AdAnchorClearance).
        style={{ paddingBottom: 'var(--ad-anchor-bottom, 0px)' }}
        className={`fixed left-0 top-0 h-full bg-zinc-900 text-zinc-100 flex flex-col border-r border-zinc-800 z-50 transition-[translate,width] duration-300 ease-drawer motion-reduce:transition-none w-full sm:max-w-xs lg:max-w-none ${
          collapsed ? 'lg:w-16' : 'lg:w-64'
        } ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Collapse toggle — hidden on mobile */}
        <div className="hidden lg:flex items-center justify-center p-2 border-b border-zinc-800">
          <button
            onClick={toggle}
            className="flex items-center justify-center gap-2 px-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 rounded-lg transition-colors"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg
              className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>

        {/* Logo */}
        <div className="relative p-3 border-b border-zinc-800 flex items-center justify-center">
          <Link href="/" onClick={closeMobile} className="transition duration-300">
            <Logo collapsed={collapsed} />
          </Link>
          {/* Close — the full-width mobile drawer covers the tap-to-close backdrop */}
          <button
            onClick={closeMobile}
            className="lg:hidden absolute right-2 top-1/2 -translate-y-1/2 p-2.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 overflow-y-auto overscroll-contain">
          {/* Tool search — hidden on the collapsed desktop rail (no room for it) */}
          {!collapsed && (
            <div className="relative mb-2">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
                placeholder="Search tools…"
                aria-label="Search tools"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-lg pl-8 pr-8 py-2 [@media(pointer:coarse)]:py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 [@media(pointer:coarse)]:p-1.5 text-zinc-500 hover:text-white rounded transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Community designs — public, for everyone; hidden while searching like My Library */}
          {!q && (
            <div className="mt-2">
              <Link
                href="/designs"
                onClick={closeMobile}
                className={`flex items-center gap-3 px-3 py-2 [@media(pointer:coarse)]:py-3 rounded-lg transition-colors ${
                  pathname.startsWith('/designs')
                    ? 'bg-zinc-800 text-white'
                    : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? 'Community Designs' : undefined}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {/* "Community", not a type name: the gallery grows categories (mobs, …). The searched-for
                    keywords ("minecraft banner designs") live in each category page's title instead. */}
                {!collapsed && <span className="text-sm">Community Designs</span>}
              </Link>
            </div>
          )}

          {/* My Library — logged-in users only, hidden while searching. Was "My Saves", which on a site
              that edits Minecraft world saves read like your worlds rather than your builds. */}
          {user && !q && (
            <div className="mt-2 mb-1">
              <Link
                // With the dot showing, go straight to what it is about.
                href={showLibraryDot ? '/saved?tab=published' : '/saved'}
                onClick={closeMobile}
                className={`relative flex items-center gap-3 px-3 py-2 [@media(pointer:coarse)]:py-3 rounded-lg transition-colors ${
                  pathname === '/saved'
                    ? 'bg-zinc-800 text-white'
                    : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? (showLibraryDot ? 'My Library — updates to your published designs' : 'My Library') : undefined}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                {!collapsed && <span className="text-sm">My Library</span>}
                {showLibraryDot && (
                  <span className={collapsed ? 'absolute top-1.5 right-2 w-2 h-2 rounded-full bg-amber-400' : 'ml-auto w-2 h-2 rounded-full bg-amber-400'}>
                    <span className="sr-only">Updates to your published designs</span>
                  </span>
                )}
              </Link>
            </div>
          )}

          {/* Tool Groups (filtered by the search query) */}
          {visibleGroups.map((group) => (
            <div key={group.label} className="mt-4">
              {!collapsed && (
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  {group.label}
                </div>
              )}
              {collapsed && (
                <div className="border-t border-zinc-800 mx-2 my-2" />
              )}

              <div className="mt-1 space-y-0.5">
                {group.tools.map((tool) => {
                  const hasFile = tool.fileType ? hasFileForTool(tool.fileType) : false;
                  const isSinglePlayerTool = tool.href === '/tools/singleplayer-data';
                  const canExpand = hasFile && (!isSinglePlayerTool || hasSinglePlayerData);
                  const showSubsections = canExpand && tool.subsections && expandedTools.includes(tool.href) && !collapsed && !q;
                  const showNoPlayerDataMessage = isSinglePlayerTool && hasFile && !hasSinglePlayerData && !collapsed;

                  return (
                    <div key={tool.href}>
                      <div className="flex items-center">
                        <Link
                          href={tool.href}
                          // ⚠️ COMPOSE with closeMobile, never replace it — dropping that call
                          // silently stops the mobile drawer closing on navigation, a UI
                          // regression with no error. `q` distinguishes a click on a search
                          // result from a click on the ordinary list; they are different
                          // behaviors and summing them would hide whether search is used.
                          onClick={() => {
                            // Report the search that led here BEFORE navigation clears the query and
                            // its cleanup cancels the debounce timer (see pendingSearch).
                            pendingSearch.current?.();
                            trackNavClick(toolSlug(tool.href), q ? 'sidebar-search' : 'sidebar');
                            closeMobile();
                          }}
                          className={`flex-1 flex items-center gap-3 px-3 py-2 [@media(pointer:coarse)]:py-3 rounded-lg transition-colors ${
                            isToolActive(tool.href)
                              ? 'bg-zinc-800 text-white'
                              : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                          } ${collapsed ? 'justify-center' : ''}`}
                          title={collapsed ? tool.name : undefined}
                        >
                          <ToolIcon icon={tool.icon} />
                          {!collapsed && <span className="text-sm">{tool.name}</span>}
                        </Link>
                        {canExpand && tool.subsections && !collapsed && !q && (
                          <button
                            onClick={() => toggleToolExpand(tool.href)}
                            aria-label={expandedTools.includes(tool.href) ? `Collapse ${tool.name} sections` : `Expand ${tool.name} sections`}
                            aria-expanded={expandedTools.includes(tool.href)}
                            className="p-2 [@media(pointer:coarse)]:p-3 text-zinc-500 hover:text-zinc-300 transition-colors"
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${
                                expandedTools.includes(tool.href) ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      {showNoPlayerDataMessage && (
                        <div className="ml-6 mt-1 px-3 py-2 border-l border-zinc-800">
                          <p className="text-xs text-zinc-500">
                            This level.dat has no singleplayer character: it is from a server, or from Minecraft 26.1+, which saves the character in players/data instead.
                          </p>
                        </div>
                      )}

                      {showSubsections && (
                        <div className="ml-6 mt-1 space-y-1 border-l border-zinc-800 pl-4">
                          {tool.subsections!
                            .filter((sub) => {
                              if (tool.href === '/tools/level-dat' && sub.section === 'modded-rules' && !hasModsDetected) {
                                return false;
                              }
                              return true;
                            })
                            .map((sub) => (
                            <Link
                              key={sub.section}
                              href={`${tool.href}?section=${sub.section}`}
                              // Reported separately from a plain sidebar click because the tool
                              // page ALSO renders its own tab strip emitting byte-identical
                              // `?section=` URLs. Without this event the two surfaces are
                              // indistinguishable — which is why the existing data cannot say
                              // whether the sidebar sub-nav is used at all.
                              onClick={() => {
                                trackNavClick(toolSlug(tool.href), 'sidebar-subsection');
                                closeMobile();
                              }}
                              className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                                pathname === tool.href && currentSection === sub.section
                                  ? 'bg-zinc-800/50 text-emerald-400'
                                  : 'text-zinc-500 hover:bg-zinc-800/30 hover:text-zinc-300'
                              }`}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* No search matches */}
          {q && visibleGroups.length === 0 && (
            <p className="px-3 py-6 text-sm text-zinc-500 text-center">
              No tools match &ldquo;{query.trim()}&rdquo;
            </p>
          )}
        </nav>

        {/* Footer */}
        <SidebarFooter collapsed={collapsed} closeMobile={closeMobile} />
      </aside>
    </>
  );
}

export default function Sidebar() {
  return (
    <Suspense fallback={
      <aside className="fixed left-0 top-0 h-full w-full sm:max-w-xs lg:max-w-none lg:w-64 z-50 bg-zinc-900 text-zinc-100 flex flex-col border-r border-zinc-800 -translate-x-full lg:translate-x-0">
        <div className="p-3 border-b border-zinc-800">
          <div className="flex items-center justify-center">
            <Logo />
          </div>
        </div>
        <nav className="flex-1 p-2">
          <div className="animate-pulse space-y-2">
            <div className="h-10 bg-zinc-800 rounded-lg"></div>
            <div className="h-10 bg-zinc-800 rounded-lg"></div>
          </div>
        </nav>
      </aside>
    }>
      <SidebarContent />
    </Suspense>
  );
}
